/** @format */

import { useState, useEffect, useCallback, useRef } from "react";
import { useBooks } from "./useBooks";
import { Book } from "../types";

const CACHE_KEY = 'shop_books_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CacheData {
  books: Array<Omit<Book, 'createdAt' | 'updatedAt'> & {
    createdAt: string;
    updatedAt: string;
  }>;
  timestamp: number;
  filters: {
    searchTerm: string;
    selectedCategory: string;
    selectedProgram: string;
    priceRange: { min: number; max: number };
    sortBy: string;
    activeTab: 'books' | 'materials';
    userProgram?: string | null;
  };
}

export const useCachedBooks = () => {
  const [cachedBooks, setCachedBooks] = useState<Book[]>([]);
  const [isLoadingFromCache, setIsLoadingFromCache] = useState(true);
  const hasLoadedFromCache = useRef(false);
  
  const {
    books,
    loading,
    error,
    hasMore,
    fetchBooks,
    loadMoreBooks,
    searchBooks,
    getCategories,
    getPrograms,
    refreshBooks,
  } = useBooks();

  // Load from cache on mount
  useEffect(() => {
    const loadFromCache = () => {
      const cachedData = localStorage.getItem(CACHE_KEY);
      if (cachedData) {
        try {
          const parsed: CacheData = JSON.parse(cachedData);
          const isExpired = Date.now() - parsed.timestamp > CACHE_TTL;
          
          if (!isExpired && parsed.books.length > 0) {
            console.log('Loading books from cache');
            // Convert date strings back to Date objects
            const booksWithDates = parsed.books.map(book => ({
              ...book,
              createdAt: book.createdAt ? new Date(book.createdAt) : new Date(),
              updatedAt: book.updatedAt ? new Date(book.updatedAt) : new Date()
            }));
            setCachedBooks(booksWithDates);
            hasLoadedFromCache.current = true;
          }
        } catch (error) {
          console.error('Error parsing cached books:', error);
          localStorage.removeItem(CACHE_KEY);
        }
      }
      setIsLoadingFromCache(false);
    };
    
    loadFromCache();
  }, []);

  // Save to cache when books change
  useEffect(() => {
    if (books.length > 0 && !isLoadingFromCache) {
      // Ensure date fields are properly serializable
      const booksForCache = books.map(book => ({
        ...book,
        createdAt: book.createdAt instanceof Date ? book.createdAt.toISOString() : book.createdAt,
        updatedAt: book.updatedAt instanceof Date ? book.updatedAt.toISOString() : book.updatedAt
      }));
      
      const cacheData: CacheData = {
        books: booksForCache,
        timestamp: Date.now(),
        filters: {
          searchTerm: '',
          selectedCategory: 'all',
          selectedProgram: 'all',
          priceRange: { min: 0, max: 1000 },
          sortBy: 'newest',
          activeTab: 'books',
          userProgram: undefined
        }
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    }
  }, [books, isLoadingFromCache]);

  // Load filters from cache
  const loadFiltersFromCache = useCallback(() => {
    const cachedData = localStorage.getItem(CACHE_KEY);
    if (cachedData) {
      try {
        const parsed: CacheData = JSON.parse(cachedData);
        return parsed.filters;
      } catch {
        return null;
      }
    }
    return null;
  }, []);

  // Save filters to cache
  const saveFiltersToCache = useCallback((filters: Partial<CacheData['filters']>) => {
    if (books.length > 0) {
      const cachedData = localStorage.getItem(CACHE_KEY);
      let existingData: CacheData;
      
      if (cachedData) {
        try {
          existingData = JSON.parse(cachedData);
        } catch {
          existingData = {
            books: [],
            timestamp: Date.now(),
            filters: {
              searchTerm: '',
              selectedCategory: 'all',
              selectedProgram: 'all',
              priceRange: { min: 0, max: 1000 },
              sortBy: 'newest',
              activeTab: 'books',
              userProgram: undefined
            }
          };
        }
      } else {
                existingData = {
          books: [],
          timestamp: Date.now(),
          filters: {
            searchTerm: '',
            selectedCategory: 'all',
            selectedProgram: 'all',
            priceRange: { min: 0, max: 1000 },
            sortBy: 'newest',
            activeTab: 'books',
            userProgram: undefined
          }
        };
      }

      const updatedData: CacheData = {
        ...existingData,
        filters: { ...existingData.filters, ...filters },
        timestamp: Date.now()
      };
      
      localStorage.setItem(CACHE_KEY, JSON.stringify(updatedData));
    }
  }, [books]);

  // Clear cache
  const clearCache = useCallback(() => {
    localStorage.removeItem(CACHE_KEY);
    setCachedBooks([]);
    hasLoadedFromCache.current = false;
  }, []);

  // Get books (prioritize cache if available)
  const getBooks = useCallback(() => {
    if (hasLoadedFromCache.current && cachedBooks.length > 0) {
      return cachedBooks;
    }
    return books;
  }, [cachedBooks, books, hasLoadedFromCache]);

  return {
    books: getBooks(),
    cachedBooks,
    loading: loading && !hasLoadedFromCache.current,
    error,
    hasMore,
    fetchBooks,
    loadMoreBooks,
    searchBooks,
    getCategories,
    getPrograms,
    refreshBooks,
    saveFiltersToCache,
    loadFiltersFromCache,
    clearCache,
    isLoadingFromCache,
    hasLoadedFromCache: hasLoadedFromCache.current
  };
};
